import { useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { isNavItemActive, type TNavItem } from '@/components/Layouts/navigation.tsx';

interface ITopNav {
  items: TNavItem[];
}

const GAP = 4;
// Width reserved for the "More" trigger when items overflow.
const MORE_WIDTH = 96;

/**
 * Horizontal primary navigation. Items that don't fit the available width
 * collapse into a "More" dropdown, so the bar never wraps or clips.
 */
export const TopNav = ({ items }: ITopNav) => {
  const { pathname } = useLocation();
  const containerRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const fit = () => {
      const widths = Array.from(measure.children).map((el) => (el as HTMLElement).offsetWidth);
      const available = container.clientWidth;

      const total = widths.reduce((sum, w, i) => sum + w + (i ? GAP : 0), 0);
      if (total <= available) {
        setVisibleCount(widths.length);
        return;
      }

      let used = MORE_WIDTH + GAP;
      let count = 0;
      for (const w of widths) {
        if (used + w + (count ? GAP : 0) > available) break;
        used += w + (count ? GAP : 0);
        count += 1;
      }
      setVisibleCount(count);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items]);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  const overflowActive = overflow.some((item) => isNavItemActive(item, pathname));

  return (
    <nav className='top-nav' ref={containerRef} aria-label='Primary'>
      {/* Hidden copy used only to measure natural item widths. */}
      <div className='top-nav__measure' ref={measureRef} aria-hidden>
        {items.map((item) => (
          <span key={item.key} className='top-nav__link'>
            {item.label}
          </span>
        ))}
      </div>

      {visible.map((item) => {
        const active = isNavItemActive(item, pathname);
        return (
          <Link
            key={item.key}
            to={item.key}
            className={`top-nav__link${active ? ' is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            {item.label}
          </Link>
        );
      })}

      {overflow.length > 0 && (
        <Dropdown
          trigger={['click']}
          placement='bottomLeft'
          menu={{
            selectedKeys: overflow.filter((i) => isNavItemActive(i, pathname)).map((i) => i.key),
            items: overflow.map((item) => ({
              key: item.key,
              icon: item.icon,
              label: <Link to={item.key}>{item.label}</Link>,
            })),
          }}
          overlayClassName='top-nav__dropdown'
        >
          <button
            type='button'
            className={`top-nav__link top-nav__more${overflowActive ? ' is-active' : ''}`}
          >
            More
            <DownOutlined className='top-nav__caret' />
          </button>
        </Dropdown>
      )}
    </nav>
  );
};
